import {
    ApolloError,
    useLazyQuery,
    useMutation,
    useQuery,
    useReactiveVar,
} from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { AppBarContext } from '@deltek/specification-client/core/Contexts/AppBarContext'
import { ProfessionalDiscipline } from '@deltek/specification-client/core/Models/professionalDisciplines'
import { GetFilterInput } from '@deltek/specification-client/core/Models/projectItem'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { TableFetchNextDataProps } from '@deltek/specification-client/core/Widgets/SpecTable'
import { Typography } from '@material-ui/core'
import Backdrop from '@material-ui/core/Backdrop/Backdrop'
import CircularProgress from '@material-ui/core/CircularProgress'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { FirmProfessionalDisciplineQuery } from 'api/graphQL/queries/firm.queries'
import { GetProjectElementsQuery } from 'api/graphQL/queries/project.queries'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import { FirmProfessionalDisciplineResponse } from 'api/graphQL/types/firm.types'
import { GetProjectElementsResponse } from 'api/graphQL/types/projectElement.types'
import clsx from 'clsx'
import { GetProjectElementsResultsInput } from 'models/getProjectElementsResultsInput'
import { WorkspaceTableItem } from 'models/workspaceTableItem'
import React, { useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { ProjectPageContext } from '../ProjectPage/components/ProjectPageContext'
import ProjectOverviewHeader from './components/ProjectOverviewHeader'
import ProjectTeamGridView from './components/ProjectTeam/ProjectTeamGridView'
import ProjectTeamMembers from './components/ProjectTeamMembers'
import WorkspaceProgressTable from './components/WorkspaceProgressTable'

const localStorageKey = (projectId: string) =>
    `ProjectsOverviewPage${projectId}`

const useStyle = makeStyles((theme: Theme) => ({
    container: {
        height: `100%`,
    },
    projectOverviewContainer: {
        width: 'calc(100% + 48px)',
        marginLeft: theme.spacing(-3),

        [theme.breakpoints.up(theme.breakpoints.values.xl + 1)]: {
            marginLeft: 0,
            width: '100%',
        },
        padding: 0,
    },
    projectTeamContainer: {
        backgroundColor: theme.palette.primary.main,
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: theme.palette.common.white,
    },
    addProjectTeamButtonCont: {
        display: 'grid',
        '& .roundButtonContainer': {
            margin: 'auto',
            marginTop: theme.spacing(0),
            marginLeft: theme.spacing(3),
        },
        '& button': {
            '&:hover': {
                color: theme.palette.common.white,
                border: '#E69C24',
                backgroundColor: '#E69C24',
            },
        },
    },
    teamsTitle: {
        margin: 'auto',
        fontSize: pxToRem(24),
        color: theme.palette.common.white,
        textTransform: 'uppercase',
        marginLeft: theme.spacing(3),
        display: 'flex',
    },
}))

const defaultPageSize: number = 100

const ProjectOverviewPage: React.FC = () => {
    const classes = useStyle()
    const {
        setCurrentPageTitle,
        setCustomActionBar,
        updateBrowserTitle,
    } = React.useContext(AppBarContext)
    const {
        projectData,
        projectLoading,
        reloadProject,
        setCategoryElementStates,
    } = React.useContext(ProjectPageContext)
    const { projectId } = useParams<{ projectId?: string }>()

    const { currentUserInfo, isReviewer } = useReactiveVar(userInfo)
    const firmId = currentUserInfo.account.accountId ?? ''

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const [skipCount, setSkipCount] = React.useState<number>(0)

    const [fetchNextLoading, setFetchNextLoading] = React.useState<boolean>(
        false
    )

    const [progressTableFilters, setProgressTableFilters] = React.useState<
        GetFilterInput[]
    >()

    const [
        getProjectElements,
        {
            data: workspaceProgressData,
            loading: workspaceProgressLoading,
            fetchMore: projectElementsFetchMore,
            networkStatus: projectElementsStatus,
        },
    ] = useLazyQuery<
        GetProjectElementsResponse,
        { input: Partial<GetProjectElementsResultsInput> }
    >(GetProjectElementsQuery, {
        fetchPolicy: 'cache-and-network',
        onError: (error: ApolloError) => {
            showGlobalSnackbar({
                variables: {
                    id: 'ProjectsOverviewPage_WorkspaceProgress',
                    variant: 'error',
                    message: 'Error loading workspace progress.',
                },
            })
        },
    })

    const fetchNextData = React.useCallback(
        ({
            totalRows,
        }: Partial<TableFetchNextDataProps<WorkspaceTableItem>>) => {
            setFetchNextLoading(true)
            let updatedSkipCount = (skipCount ?? 0) + defaultPageSize

            if (totalRows === updatedSkipCount + 1) {
                setSkipCount(updatedSkipCount)
                if (projectElementsFetchMore) {
                    projectElementsFetchMore({
                        variables: {
                            input: {
                                projectId: projectId ?? '',
                                skip: totalRows,
                                limit: defaultPageSize,
                                sortOrders: [
                                    {
                                        sortField: 'lastUpdated',
                                        sortOrder: 'desc',
                                    },
                                ],
                                filters: progressTableFilters,
                            } as Partial<GetProjectElementsResultsInput>,
                        },
                        updateQuery: (prev, { fetchMoreResult }) => {
                            if (!fetchMoreResult) return prev

                            setFetchNextLoading(false)
                            return {
                                getProjectElements: {
                                    ...prev.getProjectElements,
                                    count:
                                        fetchMoreResult.getProjectElements
                                            .count,
                                    projectElements: [
                                        ...prev.getProjectElements
                                            .projectElements!,
                                        ...fetchMoreResult.getProjectElements
                                            .projectElements!,
                                    ],
                                } as Partial<GetProjectElementsResultsInput>,
                            } as GetProjectElementsResponse
                        },
                    })
                }
            }
        },
        [
            setFetchNextLoading,
            setSkipCount,
            skipCount,
            projectElementsFetchMore,
            progressTableFilters,
        ]
    )

    const { data: firmProfessionalDisciplineData } = useQuery<
        FirmProfessionalDisciplineResponse
    >(FirmProfessionalDisciplineQuery, {
        variables: {
            firmId: firmId,
        },
    })

    const triggerProjectReload = useCallback(() => {
        setCategoryElementStates?.({})
        reloadProject?.()
    }, [reloadProject])

    useEffect(() => {
        triggerProjectReload()
    }, [triggerProjectReload])

    useEffect(() => {
        setCustomActionBar(null)
        if (!projectLoading && !!projectData && !!projectData.getProject) {
            setCurrentPageTitle(projectData?.getProject.name ?? '')
        }
        updateBrowserTitle?.(
            projectData?.getProject?.name
                ? `${projectData?.getProject?.name} - Overview`
                : 'Overview'
        )
    }, [
        setCurrentPageTitle,
        projectLoading,
        projectData,
        setCustomActionBar,
        updateBrowserTitle,
    ])

    return (
        <>
            {!projectLoading && !!projectData && !!projectData.getProject && (
                <div
                    className={classes.container}
                    data-testid="dvProjectOverview"
                >
                    <GridContainer
                        direction="row"
                        className={classes.projectOverviewContainer}
                    >
                        <GridItem lg={6} md={12} sm={12} xs={12}>
                            <ProjectOverviewHeader
                                projectData={projectData?.getProject}
                                triggerProjectReload={triggerProjectReload}
                                variant={!isReviewer ? 'full' : 'readOnly'}
                            />
                        </GridItem>
                        <GridItem lg={6} md={12} sm={12} xs={12}>
                            <WorkspaceProgressTable
                                getProjectElements={getProjectElements}
                                workspaceProgressData={workspaceProgressData}
                                projectElementsCount={
                                    workspaceProgressData?.getProjectElements
                                        .count ?? 0
                                }
                                workspaceProgressLoading={fetchNextLoading}
                                userId={currentUserInfo.kpUserId}
                                localStorageKey={localStorageKey(
                                    projectId ?? ''
                                )}
                                projectElementsStatus={projectElementsStatus}
                                fetchNextData={fetchNextData}
                                setSkipCount={setSkipCount}
                                setProgressTableFilters={
                                    setProgressTableFilters
                                }
                            />
                        </GridItem>
                    </GridContainer>
                    <GridContainer
                        direction="row"
                        className={clsx(
                            classes.projectOverviewContainer,
                            classes.projectTeamContainer
                        )}
                    >
                        <GridItem
                            lg={2}
                            md={2}
                            sm={12}
                            xs={12}
                            className={classes.addProjectTeamButtonCont}
                        >
                            <>
                                <div className={classes.teamsTitle}>
                                    <Typography variant={'h1'}>
                                        TEAMS
                                    </Typography>
                                </div>
                                <ProjectTeamMembers
                                    projectData={projectData?.getProject}
                                    variant={!isReviewer ? 'full' : 'readOnly'}
                                />
                            </>
                        </GridItem>
                        <GridItem lg={10} md={10} sm={12} xs={12}>
                            <ProjectTeamGridView
                                getProjectElements={getProjectElements}
                                professionalDisciplines={
                                    firmProfessionalDisciplineData?.firm?.professionalDisciplines.filter(
                                        (
                                            disciplineItem: Partial<
                                                ProfessionalDiscipline
                                            >
                                        ) =>
                                            disciplineItem.name?.toLowerCase() !==
                                            'all disciplines'
                                    ) ?? []
                                }
                                firmId={firmId}
                                productId={projectId}
                                variant={!isReviewer ? 'full' : 'readOnly'}
                                userId={currentUserInfo.kpUserId}
                                localStorageKey={localStorageKey(
                                    projectId ?? ''
                                )}
                            />
                        </GridItem>
                    </GridContainer>
                </div>
            )}
            <Backdrop
                className={classes.backdrop}
                open={projectLoading ?? false}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </>
    )
}

export default ProjectOverviewPage
